import React from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Image, Group, Button } from "@mantine/core";
import { ReactComponent as BoughtIcon } from "@/assets/icons/bought.svg";
import { ReactComponent as NewEditionIcon } from "@/assets/icons/new-edition.svg";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import { useCart } from "@/context/CartContext";
import {
  successNotification,
  cleanNotifications,
} from "@/helpers/notifications";
import { Issue, Publication } from "@/api/types";
import clsx from "clsx";
import dayjs from "dayjs";
import "./DetailsModal.css";

interface DetailsModalProps {
  opened: boolean;
  onClose: () => void;
  type: "issue" | "publication";
  item: Issue | Publication;
  myItems: Issue[] | Publication[];
  openFlipbook: (pdf: string) => void;
}

const isIssue = (item: Issue | Publication): item is Issue => {
  return "month" in item && "year" in item;
};

const DetailsModal: React.FC<DetailsModalProps> = ({
  opened,
  onClose,
  type,
  item,
  myItems,
  openFlipbook,
}) => {
  const navigate = useNavigate();
  const typeIsIssue = type === "issue";
  const isNew = (item: Issue | Publication) => {
    if (isIssue(item))
      return (
        item.month.toLowerCase() === dayjs().format("M") &&
        item.year === dayjs().format("YYYY")
      );
    else return dayjs(item.createdAt).format("M") === dayjs().format("M");
  };
  const { cart, addToCart, removeFromCart } = useCart();

  const formatMonth = (month: string) => {
    let formatted = dayjs(month).locale("hr").format("MMMM");
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return formatted;
  };

  const handleAddToCart = (item: Issue | Publication) => {
    addToCart({ ...item, cartId: `${type}-${item.id}`, ...{ type: type } });
    successNotification({
      message: (
        <div className="add-to-cart-notification-group">
          <span>
            {typeIsIssue ? "Revija" : "Publikacija"} je dodana u košaricu!
          </span>
          <span
            className="add-to-cart-notification-link"
            onClick={() => {
              cleanNotifications();
              navigate("/cart");
            }}
          >
            Idi na košaricu
          </span>
        </div>
      ),
    });
  };

  const handleRemoveFromCart = (id: number) => {
    removeFromCart(`${type}-${id}`);
    successNotification({
      message: `${
        typeIsIssue ? "Revija" : "Publikacija"
      } je uklonjena iz košarice`,
    });
  };

  const actionButtons = !myItems?.map((item) => item.id)?.includes(item.id) ? (
    cart
      .map((item) => item.cartId)
      .includes(
        typeIsIssue ? `issue-${item?.id}` : `publication-${item?.id}`
      ) ? (
      <Button
        variant="filled"
        color="#004a99"
        radius="xl"
        size="md"
        className="details-modal-button"
        leftSection={<DeleteIcon fill="white" />}
        onClick={() => handleRemoveFromCart(item.id)}
      >
        Ukloni iz košarice
      </Button>
    ) : (
      <Button
        variant="filled"
        color="#004a99"
        radius="xl"
        size="md"
        className="details-modal-button"
        onClick={() => handleAddToCart(item)}
      >
        Dodaj u košaricu
      </Button>
    )
  ) : (
    <Button
      variant="outline"
      color="#004a99"
      radius="xl"
      size="md"
      className="details-modal-button"
      onClick={() => openFlipbook(item.pdf.url)}
    >
      Pregledaj {typeIsIssue && "reviju"}
    </Button>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Detalji ${typeIsIssue ? "revije" : "publikacije"}`}
      centered
      size="xl"
      transitionProps={{ transition: "fade-up" }}
      classNames={{
        inner: "details-modal-inner",
        content: "details-modal-content",
        body: "details-modal-body",
        header: "details-modal-header",
        title: "details-modal-title",
        close: "details-modal-close",
      }}
    >
      <div key={item.id} className="details-modal-root">
        <div className="details-modal-container">
          <Image
            src={item.cover.url}
            className={clsx(
              "details-image",
              myItems?.map((item) => item.id)?.includes(item.id) &&
                "cursor-pointer"
            )}
            onClick={() =>
              myItems?.map((item) => item.id)?.includes(item.id) &&
              openFlipbook(item.pdf.url)
            }
          />
          <div className="details-stack">
            <div className="flex flex-col gap-1">
              {isNew(item) && (
                <Group gap={10}>
                  <NewEditionIcon />
                  <div className="text-[#918865] text-sm font-bold">
                    Novo izdanje
                  </div>
                </Group>
              )}
              <div className="text-[#0b1a29] text-base font-bold">
                {item.title}
              </div>
            </div>
            {isIssue(item) ? (
              <div className="text-[#515151] text-xs font-medium">
                {formatMonth(item.month)}, {item.year}
              </div>
            ) : (
              <div className="text-[#515151] text-xs font-medium">
                {item.author}
              </div>
            )}
            <div className="details-modal-desc desk">{item.description}</div>
            {myItems?.map((item) => item.id)?.includes(item.id) ? (
              <Group gap={5}>
                <BoughtIcon />
                <div className="text-[#515151] text-xs font-medium">
                  Kupljeno
                </div>
              </Group>
            ) : (
              <div className="text-[#004a99] text-sm font-bold">
                {item.price}€
              </div>
            )}
            <div className="details-modal-button-container">
              {actionButtons}
            </div>
          </div>
        </div>
        <div className="details-modal-desc mob">{item.description}</div>
        <div className="details-modal-mobile-button-container">
          {actionButtons}
        </div>
      </div>
    </Modal>
  );
};

export default DetailsModal;
